import { IconButton } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import styles from '../styles.module.css'
import { ILocation } from '../../../../../../../../app/entities/Location'
import { ITechnician } from '../../../../../../../../app/entities/Technician'
import { getActiveSchedulePattern } from '../../../../../../global/utils/schedule-patterns'
import SchedulePatternEditor from './SchedulePatternEditor'

interface TechnicianPanelProps {
  techniciansWithScheduling: ITechnician[]
  visibleTechnicianIds: Set<string>
  location: ILocation | null
  expandedScheduleEditors: Set<string>
  editingSchedules: Map<string, any>
  draftSchedulePatterns: Map<string, any>
  onToggleTechnicianVisibility: (technicianId: string) => void
  onToggleScheduleEditor: (technicianId: string) => void
  onStartEditingSchedule: (technicianId: string, currentPattern: any) => void
  onCancelEditingSchedule: (technicianId: string) => void
  onToggleDayInDraft: (technicianId: string, weekNumber: number, dayName: string) => void
  onAddWeekToDraft: (technicianId: string) => void
  onRemoveWeekFromDraft: (technicianId: string, weekNumber: number) => void
  onUpdateEditingData: (technicianId: string, field: 'effectiveFrom' | 'reason', value: string) => void
  onCheckChanges: (technicianId: string, workTimeData?: Pick<ITechnician, 'workDayFrom' | 'workDayTo' | 'lunchDuration'>) => void
  checkingChanges?: boolean
}

export default function TechnicianPanel({
  techniciansWithScheduling,
  visibleTechnicianIds,
  location,
  expandedScheduleEditors,
  editingSchedules,
  draftSchedulePatterns,
  onToggleTechnicianVisibility,
  onToggleScheduleEditor,
  onStartEditingSchedule,
  onCancelEditingSchedule,
  onToggleDayInDraft,
  onAddWeekToDraft,
  onRemoveWeekFromDraft,
  onUpdateEditingData,
  onCheckChanges,
  checkingChanges = false
}: TechnicianPanelProps) {
  return (
    <div className={styles.techniciansPanel}>
      <h3 className={styles.panelTitle}>
        Technicians ({visibleTechnicianIds.size} of{' '}
        {techniciansWithScheduling.length} visible)
      </h3>
      <div className={styles.techniciansList}>
        {/* Visible Technicians First */}
        {techniciansWithScheduling
          .filter((techData) =>
            visibleTechnicianIds.has(techData.objectId),
          )
          .map((techData) => (
            <div key={techData.objectId}>
              <div
                className={`${styles.technicianItem} ${
                  !visibleTechnicianIds.has(techData.objectId)
                    ? styles.hiddenTechnician
                    : ''
                }`}
              >
                <div className={styles.technicianInitials}>
                  {techData.name
                    ?.split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2) || 'T'}
                </div>
                <div className={styles.technicianDetails}>
                  <span className={styles.technicianName}>
                    {techData.name || 'Unknown Technician'}
                  </span>
                  <div
                    className={styles.scheduleLink}
                    onClick={() =>
                      onToggleScheduleEditor(techData.objectId)
                    }
                  >
                    <span>
                      {expandedScheduleEditors.has(techData.objectId)
                        ? 'Hide Schedule'
                        : 'See Schedule'}
                    </span>
                  </div>
                </div>
                <IconButton
                  size='small'
                  onClick={() =>
                    onToggleTechnicianVisibility(techData.objectId)
                  }
                  className={styles.visibilityToggle}
                  title={
                    visibleTechnicianIds.has(techData.objectId)
                      ? 'Hide from calendar'
                      : 'Show on calendar'
                  }
                >
                  {visibleTechnicianIds.has(techData.objectId) ? (
                    <VisibilityIcon className={styles.eyeIcon} />
                  ) : (
                    <VisibilityOffIcon className={styles.eyeIcon} />
                  )}
                </IconButton>
              </div>
              <SchedulePatternEditor
                techData={techData}
                location={location}
                isExpanded={expandedScheduleEditors.has(
                  techData.objectId,
                )}
                isEditing={editingSchedules.has(techData.objectId)}
                editingData={
                  editingSchedules.get(techData.objectId) || null
                }
                draftPattern={
                  draftSchedulePatterns.get(techData.objectId) || null
                }
                onToggleExpanded={() =>
                  onToggleScheduleEditor(techData.objectId)
                }
                onStartEditing={() =>
                  onStartEditingSchedule(
                    techData.objectId,
                    getActiveSchedulePattern(techData.schedulePatterns || []),
                  )
                }
                onCancelEditing={() =>
                  onCancelEditingSchedule(techData.objectId)
                }
                onToggleDayInDraft={(weekNumber, dayName) =>
                  onToggleDayInDraft(
                    techData.objectId,
                    weekNumber,
                    dayName,
                  )
                }
                onAddWeekToDraft={() =>
                  onAddWeekToDraft(techData.objectId)
                }
                onRemoveWeekFromDraft={(weekNumber) =>
                  onRemoveWeekFromDraft(techData.objectId, weekNumber)
                }
                onUpdateEditingData={(field, value) =>
                  onUpdateEditingData(techData.objectId, field, value)
                }
                onCheckChanges={(workTimeData) =>
                  onCheckChanges(techData.objectId, workTimeData)
                }
                checkingChanges={checkingChanges}
              />
            </div>
          ))}

        {/* Divider and Hidden Technicians */}
        {techniciansWithScheduling.some(
          (techData) => !visibleTechnicianIds.has(techData.objectId),
        ) && (
          <>
            <div className={styles.divider}>
              <span className={styles.dividerText}>
                Not currently visible on calendar
              </span>
            </div>
            {techniciansWithScheduling
              .filter(
                (techData) =>
                  !visibleTechnicianIds.has(techData.objectId),
              )
              .map((techData) => (
                <div
                  key={techData.objectId}
                  className={`${styles.technicianItem} ${
                    !visibleTechnicianIds.has(techData.objectId)
                      ? styles.hiddenTechnician
                      : ''
                  }`}
                >
                  <div className={styles.technicianInitials}>
                    {techData.name
                      ?.split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2) || 'T'}
                  </div>
                  <div className={styles.technicianDetails}>
                    <span className={styles.technicianName}>
                      {techData.name || 'Unknown Technician'}
                    </span>
                  </div>
                  <IconButton
                    size='small'
                    onClick={() =>
                      onToggleTechnicianVisibility(techData.objectId)
                    }
                    className={styles.visibilityToggle}
                    title={
                      visibleTechnicianIds.has(techData.objectId)
                        ? 'Hide from calendar'
                        : 'Show on calendar'
                    }
                  >
                    {visibleTechnicianIds.has(techData.objectId) ? (
                      <VisibilityIcon className={styles.eyeIcon} />
                    ) : (
                      <VisibilityOffIcon className={styles.eyeIcon} />
                    )}
                  </IconButton>
                </div>
              ))}
          </>
        )}

        {techniciansWithScheduling.length === 0 && (
          <p className={styles.emptyState}>
            No active technicians found
          </p>
        )}
      </div>
    </div>
  )
}